import React, { useRef, useState, useCallback, useEffect, useMemo } from 'react';
import TiptapEditor from './editor/TiptapEditor';
import EnhancementSidebar from './editor/EnhancementSidebar';
import FeatherIcon from 'feather-icons-react';
import { trackPromise } from 'react-promise-tracker';
import { apiCall } from '../utils/apiUtils';
import { 
  streamBlogGeneration, 
  streamEnhancementGeneration,
  generateBlogNonStreaming,
  generateEnhancementNonStreaming 
} from '../services/streamingService';
import {
  peekStoredSession as peekAnonymousSession,
  refreshSession as refreshAnonymousSession,
  getOrCreateSession as getOrCreateAnonymousSession,
} from '../services/anonymousSessionService';
import { useAuth } from './authProvider/AuthContext';
import { ReactComponent as FastSeoCopyIcon } from '../images/fastseocopy-icon-frame.svg';
import { useBrandVoice } from '../contexts/BrandVoiceContext';
import SettingsPanel from './SettingsPanel';
import PaywallModal from './PaywallModal';
import AnonymousSignUpModal from './AnonymousSignUpModal';
import SeoScoreMeter from './SeoScoreMeter';
import { computeSeoAnatomyScore } from '../utils/seoAnatomyScore';
import { normalizeBlogHtml } from '../utils/normalizeBlogHtml';
import TurndownService from 'turndown';
import {
  getSafeFilename,
  buildHtmlDocument,
  triggerDownload,
  openPrintableHtmlForPdf,
} from './exportBlogUtils';
import './CopyDownload.css';

const ATTRIBUTION_TEXT = '\n\n---\nGenerated with AITrend SEO · https://aitrend.io';
const ATTRIBUTION_HTML_FOOTER = 'Generated with AITrend SEO · https://aitrend.io';

const turndownService = new TurndownService();

/**
 * Extracts <title>, <meta name="description">, and <script type="application/ld+json">
 * from the AI response and returns them alongside the body content with those tags stripped out.
 * The schemaMarkup string (without script tags) is intended for injection into the HTML download.
 */
function parseAndStripMetaTags(content) {
  const titleMatch = content.match(/<title>([\s\S]*?)<\/title>/i);
  const metaMatch = content.match(/<meta\s+name="description"\s+content="([\s\S]*?)"/i);
  const schemaMatch = content.match(/<script\s+type="application\/ld\+json">([\s\S]*?)<\/script>/i);
  const seoTitle = titleMatch ? titleMatch[1].trim() : '';
  const seoDescription = metaMatch ? metaMatch[1].trim() : '';
  const schemaMarkup = schemaMatch ? schemaMatch[1].trim() : '';
  const bodyContent = content
    .replace(/<title>[\s\S]*?<\/title>/i, '')
    .replace(/<meta\s+name="description"[^>]*>/i, '')
    .replace(/<script\s+type="application\/ld\+json">[\s\S]*?<\/script>/gi, '')
    .trim();
  return { seoTitle, seoDescription, schemaMarkup, bodyContent };
}

export default function CopyDownload({
  onResponse, 
  h2Data, 
  h1Selected,
  selectedTrendQuery = '',
  setH2Data, 
  apiUrl, 
  apiSecret,
  isStreaming: externalIsStreaming,
  setIsStreaming: setExternalIsStreaming,
  currentBlog,
  setCurrentBlog,
  refreshSidebar
}) {
  const editorRef = useRef(null);
  const [isStreaming, setIsStreaming] = useState(false);
  const [isGeneratingEnhancement, setIsGeneratingEnhancement] = useState(false);
  const [seoTitle, setSeoTitle] = useState('');
  const [seoDescription, setSeoDescription] = useState('');
  const [schemaMarkup, setSchemaMarkup] = useState('');
  const [seoScore, setSeoScore] = useState(null);
  const [seoTitleCopied, setSeoTitleCopied] = useState(false);
  const [seoDescCopied, setSeoDescCopied] = useState(false);
  const [streamController, setStreamController] = useState(null);
  const [currentContent, setCurrentContent] = useState('');
  const [lastProcessedHeadline, setLastProcessedHeadline] = useState(null);
  const [isAutoTriggering, setIsAutoTriggering] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedContent, setLastSavedContent] = useState('');
  const [copySuccess, setCopySuccess] = useState(false);
  const [copyMenuOpen, setCopyMenuOpen] = useState(false);
  const [downloadMenuOpen, setDownloadMenuOpen] = useState(false);
  const copyMenuRef = useRef(null);
  const downloadMenuRef = useRef(null);
  const [paywallVisible, setPaywallVisible] = useState(false);
  const [paywallData, setPaywallData] = useState({ availableCredits: 0, requiredCredits: 5 });
  const [regenToast, setRegenToast] = useState('');
  // Anonymous trial modal — shown when a non-logged-in visitor hits the free
  // blog cap or has just used their last free blog.
  const [anonModalVisible, setAnonModalVisible] = useState(false);
  const [anonModalData, setAnonModalData] = useState({
    reason: 'limit-reached',
    blogsUsed: 2,
    freeLimit: 2,
    bonusBlogs: 4,
  });
  const [anonSession, setAnonSession] = useState(() => peekAnonymousSession());
  const { isAuthenticated } = useAuth();
  /** Completed blog generations for the current headline (drives free-regen billing on the server). */
  const blogGenCountRef = useRef(0);
  const [blogGenCount, setBlogGenCount] = useState(0);
  const headlineTriggerInProgress = useRef(false);
  const { getBlogGenerationPayload } = useBrandVoice();

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (copyMenuRef.current && !copyMenuRef.current.contains(e.target)) {
        setCopyMenuOpen(false);
      }
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(e.target)) {
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setCopyMenuOpen(false);
        setDownloadMenuOpen(false);
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, []);

  const bumpCompletedGenerations = useCallback(() => {
    blogGenCountRef.current += 1;
    setBlogGenCount(blogGenCountRef.current);
  }, []);

  // After every anonymous generation, re-sync the cached counter so the UI
  // reflects server state and decide whether to pop the incentive modal.
  const syncAnonymousAfterGeneration = useCallback(async () => {
    if (isAuthenticated) return;
    try {
      const refreshed = await refreshAnonymousSession();
      if (!refreshed) return;
      setAnonSession(refreshed);
      const used = refreshed.blogsGenerated ?? 0;
      const limit = refreshed.freeLimit ?? 2;
      const bonus = refreshed.signupBonusBlogs ?? 4;
      if (used >= limit) {
        setAnonModalData({
          reason: 'limit-reached',
          blogsUsed: used,
          freeLimit: limit,
          bonusBlogs: bonus,
        });
        setAnonModalVisible(true);
      } else if (used === limit - 1) {
        // Soft incentive: they've got exactly one free blog left, nudge
        // them toward signing up while the experience is fresh.
        setAnonModalData({
          reason: 'incentive',
          blogsUsed: used,
          freeLimit: limit,
          bonusBlogs: bonus,
        });
        setAnonModalVisible(true);
      }
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.info('Anonymous sync after generation failed:', err?.message);
      }
    }
  }, [isAuthenticated]);

  useEffect(() => {
    blogGenCountRef.current = 0;
    setBlogGenCount(0);
  }, [h1Selected]);

  useEffect(() => {
    if (currentBlog?.content && currentBlog.h1Title === h1Selected) {
      blogGenCountRef.current = Math.max(blogGenCountRef.current, 1);
      setBlogGenCount(blogGenCountRef.current);
    }
  }, [currentBlog, h1Selected]);

  useEffect(() => {
    if (!regenToast) return undefined;
    const t = setTimeout(() => setRegenToast(''), 8000);
    return () => clearTimeout(t);
  }, [regenToast]);

  const regenBillingHint = useMemo(() => {
    if (!h1Selected) return '';
    if (!isAuthenticated) {
      const limit = anonSession?.freeLimit ?? 2;
      const used = anonSession?.blogsGenerated ?? 0;
      const remaining = Math.max(0, limit - used);
      if (remaining === 0) return 'You have used your free blogs. Sign up to keep generating.';
      if (remaining === 1) return `${remaining} free blog remaining. Sign up for ${anonSession?.signupBonusBlogs ?? 4} more on the house.`;
      return `${remaining} free blogs remaining. No sign-up required.`;
    }
    const loaded = Boolean(currentBlog?.content && currentBlog.h1Title === h1Selected);
    if (blogGenCount === 0 && !loaded) {
      return 'First generation for this headline uses 5 credits. The next two regenerations are free; after that, each costs 5 credits.';
    }
    if (blogGenCount === 0 && loaded) {
      return 'You have 2 free regenerations for this headline. After that, each regeneration costs 5 credits.';
    }
    if (blogGenCount === 1) return '2 free regenerations remaining for this headline. Then 5 credits per regeneration.';
    if (blogGenCount === 2) return '1 free regeneration remaining. After that, 5 credits per regeneration.';
    if (blogGenCount >= 3) return 'Further regenerations cost 5 credits each.';
    return '';
  }, [h1Selected, blogGenCount, currentBlog, isAuthenticated, anonSession]);

  // Extract initial content for editor (only string content)
  useEffect(() => {
    if (typeof h2Data === 'string' && h2Data) {
      setCurrentContent(h2Data);
    }
  }, [h2Data]);

  // Compute the SEO anatomy score from the full generated HTML (title + meta +
  // body + schema). Cheap and deterministic, so it is safe to run on each
  // generation and whenever a saved blog is loaded.
  const computeAndSetScore = useCallback((fullHtml, keyword) => {
    if (!fullHtml) {
      setSeoScore(null);
      return;
    }
    const result = computeSeoAnatomyScore({
      html: fullHtml,
      primaryKeyword: keyword || h1Selected || '',
    });
    setSeoScore(result);
  }, [h1Selected]);

  // Reassemble the full blog HTML (SEO title + meta + body + JSON-LD schema)
  // so the editor body stays clean while persistence keeps the SEO metadata.
  // Without this, auto-save would strip the title/meta/schema and the SEO
  // score (and meta panel) would lose them on reload.
  const buildFullBlogContent = useCallback((body) => {
    const parts = [];
    if (seoTitle) parts.push(`<title>${seoTitle}</title>`);
    if (seoDescription) parts.push(`<meta name="description" content="${seoDescription}">`);
    parts.push(body || '');
    if (schemaMarkup) parts.push(`<script type="application/ld+json">${schemaMarkup}</script>`);
    return parts.join('\n');
  }, [seoTitle, seoDescription, schemaMarkup]);

  // Tracks which blog id is currently loaded into the editor so we only reset
  // the editor / restore SEO meta when a genuinely different blog is opened
  // (not on every currentBlog update triggered by auto-save).
  const loadedBlogIdRef = useRef(null);

  // Load a saved blog: split the stored full HTML back into SEO meta + body,
  // restore the meta state (for the panel and score), show the clean body in
  // the editor, and score the full content.
  useEffect(() => {
    if (!currentBlog || !currentBlog.content) return;
    if (loadedBlogIdRef.current === currentBlog.id) return;
    loadedBlogIdRef.current = currentBlog.id;

    const {
      seoTitle: parsedTitle,
      seoDescription: parsedDesc,
      schemaMarkup: parsedSchema,
      bodyContent,
    } = parseAndStripMetaTags(currentBlog.content);
    const body = normalizeBlogHtml(bodyContent || currentBlog.content);

    setSeoTitle(parsedTitle || '');
    setSeoDescription(parsedDesc || '');
    setSchemaMarkup(parsedSchema || '');

    setCurrentContent(body);
    setLastSavedContent(body);

    if (editorRef.current) {
      editorRef.current.setContent(body);
    }

    setH2Data(body);
    computeAndSetScore(currentBlog.content, currentBlog.h1Title);
  }, [currentBlog, setH2Data, computeAndSetScore]);

  // `content` is the full HTML persisted to the server (SEO title + meta +
  // body + schema). `bodyBaseline` is the editor body that maps to it and is
  // used for dirty-tracking so auto-save does not loop.
  const saveBlogContent = useCallback(async (content, bodyBaseline, isNewBlog = true) => {
    if (!content || !h1Selected || isSaving) return;
    const baseline = bodyBaseline != null ? bodyBaseline : content;

    setIsSaving(true);
    try {
      if (isNewBlog || !currentBlog) {
        // Create new blog
        const blogData = {
          h1Title: h1Selected,
          content: content,
          isStarred: false
        };

        const response = await apiCall('/save-blog', JSON.stringify(blogData), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' }
        });

        if (response && response.blog) {
          loadedBlogIdRef.current = response.blog.id;
          setCurrentBlog(response.blog);
          setLastSavedContent(baseline);
          // Refresh sidebar to show the new blog
          if (refreshSidebar) {
            refreshSidebar();
          }
        }
      } else {
        // Update existing blog
        const blogData = {
          id: currentBlog.id,
          h1Title: h1Selected,
          content: content,
          isStarred: currentBlog.isStarred
        };

        const response = await apiCall('/update-blog', JSON.stringify(blogData), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' }
        });

        if (response && response.blog) {
          loadedBlogIdRef.current = response.blog.id;
          setCurrentBlog(response.blog);
          setLastSavedContent(baseline);
          // Refresh sidebar to reflect the update
          if (refreshSidebar) {
            refreshSidebar();
          }
        }
      }
    } catch (error) {
      console.error('Error saving blog:', error);
    } finally {
      setIsSaving(false);
    }
  }, [h1Selected, isSaving, currentBlog, refreshSidebar, setCurrentBlog, setLastSavedContent]);

  // Auto-save blog when the editor body changes (with debounce). We persist
  // the reassembled full HTML (so SEO meta survives) but dirty-track against
  // the body baseline.
  useEffect(() => {
    if (!currentBlog || !currentContent || currentContent === lastSavedContent || isSaving) {
      return;
    }

    const timeoutId = setTimeout(async () => {
      await saveBlogContent(buildFullBlogContent(currentContent), currentContent, false);
    }, 2000); // 2 second debounce

    return () => clearTimeout(timeoutId);
  }, [currentContent, currentBlog, lastSavedContent, isSaving, saveBlogContent, buildFullBlogContent]);

  const showPaywall = useCallback((error) => {
    setPaywallData({
      availableCredits: error?.availableCredits ?? 0,
      requiredCredits: error?.requiredCredits ?? 5,
    });
    setPaywallVisible(true);
  }, []);

  // Refresh anonymous session counter on mount so we respect the server
  // state (e.g. if the user generated from another tab or cleared cookies
  // and came back). No-op for signed-in users.
  useEffect(() => {
    if (isAuthenticated) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const refreshed = await refreshAnonymousSession();
        if (!cancelled && refreshed) setAnonSession(refreshed);
      } catch (err) {
        // Not fatal — we'll (re)mint a session when the user tries to
        // generate. Log only in dev to keep the console clean in prod.
        if (process.env.NODE_ENV === 'development') {
          console.info('Anonymous session refresh skipped:', err?.message);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  const showAnonModal = useCallback((reason, overrides = {}) => {
    const stored = peekAnonymousSession();
    const freeLimit = overrides.freeLimit ?? stored?.freeLimit ?? 2;
    const blogsUsed = overrides.blogsUsed ?? stored?.blogsGenerated ?? 0;
    const bonusBlogs = overrides.bonusBlogs ?? stored?.signupBonusBlogs ?? 4;
    setAnonModalData({ reason, blogsUsed, freeLimit, bonusBlogs });
    setAnonModalVisible(true);
  }, []);

  const handleAnonLimitError = useCallback((error) => {
    showAnonModal('limit-reached', {
      freeLimit: error?.freeLimit,
      blogsUsed: error?.blogsGenerated,
    });
  }, [showAnonModal]);

  const handleRefresh = useCallback(async () => {
    
    if (!h1Selected) {
      console.error('No H1 selected for refresh');
      return;
    }

    if (isStreaming) {
      return;
    }

    setSeoTitle('');
    setSeoDescription('');
    setSchemaMarkup('');
    setSeoScore(null);

    // Pre-flight gate for anonymous visitors: if they've already consumed
    // their free-trial slots, show the signup modal instead of hitting the
    // server (which would also reject them, but round-tripping wastes time
    // and creates unnecessary load).
    if (!isAuthenticated) {
      try {
        let session = peekAnonymousSession();
        if (!session) {
          session = await getOrCreateAnonymousSession();
          setAnonSession(session);
        }
        const limit = session?.freeLimit ?? 2;
        const used = session?.blogsGenerated ?? 0;
        if (used >= limit) {
          showAnonModal('limit-reached', { freeLimit: limit, blogsUsed: used });
          return;
        }
      } catch (err) {
        if (err?.code === 'ANON_IP_LIMIT_REACHED') {
          showAnonModal('limit-reached', {
            freeLimit: err?.freeLimit ?? 2,
            blogsUsed: err?.freeLimit ?? 2,
          });
          return;
        }
        console.error('Anonymous session precheck failed:', err);
      }
    }

    if (streamController) {
      streamController.stop();
    }

    setIsStreaming(true);
    if (setExternalIsStreaming) {
      setExternalIsStreaming(true);
    }
    setCurrentContent('');

    try {
      // Clear the editor and set streaming mode
      if (editorRef.current) {
        editorRef.current.setContent('');
        editorRef.current.setStreamingMode(true);
      }

      // Get brand voice settings
      const blogPayload = getBlogGenerationPayload();
      const prior = blogGenCountRef.current;
      if (!isAuthenticated) {
        const stored = peekAnonymousSession();
        const limit = stored?.freeLimit ?? 2;
        const used = stored?.blogsGenerated ?? 0;
        const remainingAfter = Math.max(0, limit - used - 1);
        if (remainingAfter === 0) {
          setRegenToast(`Using your last free blog. Sign up for ${stored?.signupBonusBlogs ?? 4} more.`);
        } else {
          setRegenToast(`Free blog ${used + 1} of ${limit}. ${remainingAfter} more free blog${remainingAfter === 1 ? '' : 's'} after this.`);
        }
      } else if (prior === 0) {
        setRegenToast('This generation uses 5 credits from your balance.');
      } else if (prior === 1) {
        setRegenToast('Free regeneration (1 of 2). The next regeneration is also free.');
      } else if (prior === 2) {
        setRegenToast('Free regeneration (2 of 2). The next regeneration will cost 5 credits.');
      } else {
        setRegenToast('This regeneration uses 5 credits from your balance.');
      }

      // Try streaming first
      const controller = await streamBlogGeneration(h1Selected, {
        priorCompletedGenerations: prior,
        brandVoice: blogPayload,
        trendData: selectedTrendQuery,
        onContent: (content) => {
          if (editorRef.current && content) {
            editorRef.current.appendStreamingContent(content);
          }
          setCurrentContent(prev => prev + content);
        },
        onComplete: async (fullContent) => {
          setIsStreaming(false);
          if (setExternalIsStreaming) {
            setExternalIsStreaming(false);
          }
          
          if (editorRef.current) {
            editorRef.current.setStreamingMode(false);
          }
          
          if (fullContent) {
            const { seoTitle: parsedTitle, seoDescription: parsedDesc, schemaMarkup: parsedSchema, bodyContent } = parseAndStripMetaTags(fullContent);
            if (parsedTitle) setSeoTitle(parsedTitle);
            if (parsedDesc) setSeoDescription(parsedDesc);
            if (parsedSchema) setSchemaMarkup(parsedSchema);
            computeAndSetScore(fullContent);
            const contentForEditor = normalizeBlogHtml(bodyContent || fullContent);

            if (editorRef.current) {
              const editor = editorRef.current.getEditor();
              if (editor) {
                editor.commands.clearContent();
                setTimeout(() => {
                  try {
                    editor.commands.setContent(contentForEditor);
                  } catch (error) {
                    console.error('Direct editor setContent failed:', error);
                  }
                }, 10);
              } else {
                editorRef.current.setContent(contentForEditor);
              }
            }
            setH2Data(contentForEditor);
            setCurrentContent(contentForEditor);
            bumpCompletedGenerations();

            // Auto-save is only available to signed-in users, so skip the
            // /save-blog call entirely for anonymous visitors — otherwise
            // apiUtils will throw NO_ACCOUNT and surface a scary error even
            // though the generation itself succeeded.
            if (!isAuthenticated) {
              syncAnonymousAfterGeneration();
            } else if (isAutoTriggering || !currentBlog) {
              setCurrentBlog(null); // Clear current blog to force new save
              await saveBlogContent(fullContent, contentForEditor, true);
            }
          } else {
            const streamedContent = editorRef.current ? editorRef.current.getContent() : '';
            setH2Data(streamedContent);
            setCurrentContent(streamedContent);
            if (streamedContent && streamedContent.length > 50) {
              bumpCompletedGenerations();
            }

            if (!isAuthenticated) {
              if (streamedContent && streamedContent.length > 50) {
                syncAnonymousAfterGeneration();
              }
            } else if (streamedContent && streamedContent.length > 100 && (isAutoTriggering || !currentBlog)) {
              setCurrentBlog(null); // Clear current blog to force new save
              await saveBlogContent(streamedContent, streamedContent, true);
            }
          }
          setStreamController(null);
          setIsAutoTriggering(false);
          headlineTriggerInProgress.current = false;
        },
        onError: async (error) => {
          console.error('Streaming error, falling back to non-streaming:', error);

          // Show paywall immediately — no need to fall back
          if (error?.isPaywall) {
            showPaywall(error);
            setIsStreaming(false);
            if (setExternalIsStreaming) setExternalIsStreaming(false);
            if (editorRef.current) editorRef.current.setStreamingMode(false);
            setIsAutoTriggering(false);
            headlineTriggerInProgress.current = false;
            setStreamController(null);
            return;
          }

          // Anonymous trial exhausted server-side — open the signup modal.
          if (error?.isAnonymousLimit) {
            handleAnonLimitError(error);
            setIsStreaming(false);
            if (setExternalIsStreaming) setExternalIsStreaming(false);
            if (editorRef.current) editorRef.current.setStreamingMode(false);
            setIsAutoTriggering(false);
            headlineTriggerInProgress.current = false;
            setStreamController(null);
            return;
          }
          
          setIsStreaming(false);
          if (setExternalIsStreaming) {
            setExternalIsStreaming(false);
          }
          if (editorRef.current) {
            editorRef.current.setStreamingMode(false);
          }
          setIsAutoTriggering(false);
          headlineTriggerInProgress.current = false;
          
          // Fallback to non-streaming
          try {
            const rawContent = await trackPromise(
              generateBlogNonStreaming(h1Selected, blogPayload, {
                priorCompletedGenerations: blogGenCountRef.current,
                trendData: selectedTrendQuery,
              })
            );

            const { seoTitle: parsedTitle, seoDescription: parsedDesc, schemaMarkup: parsedSchema, bodyContent } = parseAndStripMetaTags(rawContent);
            if (parsedTitle) setSeoTitle(parsedTitle);
            if (parsedDesc) setSeoDescription(parsedDesc);
            if (parsedSchema) setSchemaMarkup(parsedSchema);
            computeAndSetScore(rawContent);
            const content = normalizeBlogHtml(bodyContent || rawContent);

            if (editorRef.current) {
              editorRef.current.setContent(content);
            }
            setH2Data(content);
            setCurrentContent(content);
            bumpCompletedGenerations();
            if (!isAuthenticated) {
              syncAnonymousAfterGeneration();
            }
          } catch (fallbackError) {
            if (fallbackError?.isPaywall) {
              showPaywall(fallbackError);
              return;
            }
            if (fallbackError?.isAnonymousLimit) {
              handleAnonLimitError(fallbackError);
              return;
            }
            console.error('Fallback generation failed:', fallbackError);
            
            // Final fallback to original API
            try {
              const prompt = { 
                h1: h1Selected,
                priorCompletedGenerations: blogGenCountRef.current,
                ...getBlogGenerationPayload()
              };
              const newH2Data = await trackPromise(
                apiCall('/openai-h2', JSON.stringify(prompt), {
                  headers: { "x-functions-key": apiSecret }
                })
              );
              
              setH2Data(newH2Data);
              setCurrentContent(newH2Data);
              bumpCompletedGenerations();
              
              if (editorRef.current) {
                editorRef.current.setContent(newH2Data);
              }
            } catch (apiError) {
              console.error('All generation methods failed:', apiError);
            }
          }
          
          setStreamController(null);
        }
      });

      setStreamController(controller);

    } catch (error) {
      console.error('Error starting blog generation:', error);
      if (error?.isPaywall) {
        showPaywall(error);
      } else if (error?.isAnonymousLimit) {
        handleAnonLimitError(error);
      }
      setIsStreaming(false);
      if (setExternalIsStreaming) {
        setExternalIsStreaming(false);
      }
      if (editorRef.current) {
        editorRef.current.setStreamingMode(false);
      }
      setIsAutoTriggering(false); // Reset the flag
      headlineTriggerInProgress.current = false; // Reset ref
    }
  }, [h1Selected, streamController, setExternalIsStreaming, apiSecret, setH2Data, isStreaming, isAutoTriggering, currentBlog, setCurrentBlog, saveBlogContent, getBlogGenerationPayload, showPaywall, bumpCompletedGenerations, isAuthenticated, showAnonModal, handleAnonLimitError, syncAnonymousAfterGeneration, selectedTrendQuery, computeAndSetScore]);

  // Detect when a new headline is selected and start streaming (exactly like refresh button)
  useEffect(() => {
    if (h1Selected && h1Selected !== lastProcessedHeadline && h2Data === null && !isStreaming && !headlineTriggerInProgress.current) {
      headlineTriggerInProgress.current = true;
      setLastProcessedHeadline(h1Selected);
      setIsAutoTriggering(true);
      setTimeout(() => {
        handleRefresh();
      }, 200);
    }
  }, [h1Selected, h2Data, lastProcessedHeadline, handleRefresh, isStreaming, currentContent, streamController]);

  const runClipboardCopy = useCallback((text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopySuccess(true);
      setTimeout(() => setCopySuccess(false), 2000);
    }).catch((err) => {
      console.error('Failed to copy: ', err);
      setCopySuccess(false);
    });
  }, []);

  const handleCopyPlain = useCallback(() => {
    if (!editorRef.current) return;
    const content = editorRef.current.getContent({ format: 'text' });
    runClipboardCopy(content);
  }, [runClipboardCopy]);

  const handleCopyHtml = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.getContent({ format: 'html' });
    runClipboardCopy(html);
  }, [runClipboardCopy]);

  const handleDownloadTxt = useCallback(() => {
    if (!editorRef.current) return;
    const text = editorRef.current.getContent({ format: 'text' });
    const withAttribution = text + ATTRIBUTION_TEXT;
    const blob = new Blob([withAttribution], { type: 'text/plain;charset=utf-8' });
    triggerDownload(blob, `${getSafeFilename(h1Selected)}.txt`);
  }, [h1Selected]);

  const handleDownloadHtml = useCallback(() => {
    if (!editorRef.current) return;
    const bodyHtml = editorRef.current.getContent({ format: 'html' });
    const doc = buildHtmlDocument(h1Selected, bodyHtml, ATTRIBUTION_HTML_FOOTER, schemaMarkup);
    const blob = new Blob([doc], { type: 'text/html;charset=utf-8' });
    triggerDownload(blob, `${getSafeFilename(h1Selected)}.html`);
  }, [h1Selected, schemaMarkup]);

  const handleDownloadMd = useCallback(() => {
    if (!editorRef.current) return;
    const html = editorRef.current.getContent({ format: 'html' });
    const md = turndownService.turndown(html) + '\n\n---\n' + ATTRIBUTION_HTML_FOOTER;
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    triggerDownload(blob, `${getSafeFilename(h1Selected)}.md`);
  }, [h1Selected]);

  const handleDownloadPdf = useCallback(() => {
    if (!editorRef.current) return;
    const bodyHtml = editorRef.current.getContent({ format: 'html' });
    const doc = buildHtmlDocument(h1Selected, bodyHtml, ATTRIBUTION_HTML_FOOTER, schemaMarkup);
    const ok = openPrintableHtmlForPdf(doc);
    if (!ok) {
      console.warn('Could not open print window; allow pop-ups to save as PDF.');
    }
  }, [h1Selected, schemaMarkup]);

  const handleEnhancementSelect = useCallback(async (enhancement) => {
    if (!editorRef.current || isGeneratingEnhancement) return;

    setIsGeneratingEnhancement(true);

    try {
      const blogContext = editorRef.current.getContent({ format: 'text' });

      // Try streaming enhancement generation
      await streamEnhancementGeneration(enhancement, blogContext, {
        onContent: (content) => {
          // For streaming enhancements, we collect the content and insert it at the end
        },
        onComplete: async (generatedContent) => {
          if (generatedContent && editorRef.current) {
            // Process the content based on enhancement type
            processEnhancementContent(enhancement, generatedContent);
          }
          setIsGeneratingEnhancement(false);
        },
        onError: async (error) => {
          console.error('Enhancement streaming error, falling back:', error);
          
          try {
            // Fallback to non-streaming
            const generatedContent = await generateEnhancementNonStreaming(enhancement, blogContext);
            if (generatedContent && editorRef.current) {
              processEnhancementContent(enhancement, generatedContent);
            }
          } catch (fallbackError) {
            console.error('Enhancement generation failed:', fallbackError);
          }
          
          setIsGeneratingEnhancement(false);
        }
      });

    } catch (error) {
      console.error('Error generating enhancement:', error);
      setIsGeneratingEnhancement(false);
    }
  }, [isGeneratingEnhancement]);

  const processEnhancementContent = (enhancement, content) => {
    if (!editorRef.current) return;

    switch (enhancement.id) {
      case 'orderedList':
      case 'bulletList':
      case 'blockquote':
        // These come as HTML, insert directly
        editorRef.current.insertEnhancement(enhancement.id);
        // Then replace the placeholder with actual content
        setTimeout(() => {
          const editor = editorRef.current.getEditor();
          if (editor) {
            const currentHTML = editor.getHTML();
            const updatedHTML = currentHTML.replace(
              new RegExp(`<${enhancement.id === 'blockquote' ? 'blockquote' : (enhancement.id === 'orderedList' ? 'ol' : 'ul')}[^>]*>.*?</${enhancement.id === 'blockquote' ? 'blockquote' : (enhancement.id === 'orderedList' ? 'ol' : 'ul')}>`, 's'),
              content
            );
            editor.commands.setContent(updatedHTML);
          }
        }, 100);
        break;
        
      case 'ctaButton':
      case 'ctaBanner':
        // These need the text content
        editorRef.current.insertEnhancement(enhancement.id, { text: content.trim() });
        break;
        
      case 'visual':
      case 'dataVisualization':
        // Insert placeholder with the generated description
        editorRef.current.insertEnhancement(enhancement.id);
        // Update the placeholder with the description
        setTimeout(() => {
          const editor = editorRef.current.getEditor();
          if (editor) {
            const currentHTML = editor.getHTML();
            const placeholderClass = enhancement.id === 'visual' ? 'visual-placeholder' : 'data-viz-placeholder';
            const updatedHTML = currentHTML.replace(
              new RegExp(`<div class="${placeholderClass}">.*?</div>`, 's'),
              `<div class="${placeholderClass}"><p>${content}</p></div>`
            );
            editor.commands.setContent(updatedHTML);
          }
        }, 100);
        break;
        
      default:
        // For other types, insert as is
        editorRef.current.insertEnhancement(enhancement.id);
    }
  };

  const handleContentChange = (content) => {
    setCurrentContent(content);
  };

  const handleStopGeneration = () => {
    if (streamController) {
      streamController.stop();
      setStreamController(null);
    }
    setIsStreaming(false);
    if (setExternalIsStreaming) {
      setExternalIsStreaming(false);
    }
    
    if (editorRef.current) {
      editorRef.current.setStreamingMode(false);
    }
  };

  return (
    <div className='copydownload'>
      {paywallVisible && (
        <PaywallModal
          onClose={() => setPaywallVisible(false)}
          availableCredits={paywallData.availableCredits}
          requiredCredits={paywallData.requiredCredits}
        />
      )}

      {anonModalVisible && (
        <AnonymousSignUpModal
          onClose={() => setAnonModalVisible(false)}
          blogsUsed={anonModalData.blogsUsed}
          freeLimit={anonModalData.freeLimit}
          bonusBlogs={anonModalData.bonusBlogs}
          reason={anonModalData.reason}
        />
      )}

      <h1>From trend to SEO article in 60 seconds.</h1>
      <h2>Pay only for what you publish. No subscription.</h2>

      <div className='editor-container'>
        {/* Clean Control Bar */}
        <div className="editor-control-bar">
          {/* Left Section: Icon + Status */}
          <div className="control-bar-left">
            <FastSeoCopyIcon className="blog-status-icon" />
            <div className="editing-mode-container">
              <span className="editing-mode-label">Editing mode:</span>
              <span className={`editing-mode-status ${isStreaming ? 'generating' : (currentBlog ? 'active' : 'ready')}`}>
                {isStreaming ? 'GENERATING' : (currentBlog ? 'ACTIVE' : 'READY')}
              </span>
            </div>
          </div>

          {/* Separator */}
          <div className="control-bar-separator"></div>

          {/* Center Section: Blog Title */}
          {currentBlog && (
            <>
              <div className="control-bar-center">
                <span className="blog-title">
                  <strong>{currentBlog.h1Title}</strong>
                  {currentBlog.isStarred && <span className="star-badge">⭐</span>}
                </span>
              </div>

              {/* Separator */}
              <div className="control-bar-separator"></div>
            </>
          )}

          {/* Right Section: Action Buttons */}
          <div className="control-bar-right">
            {!isStreaming && seoScore && (
              <>
                <SeoScoreMeter score={seoScore} />
                <div className="control-bar-separator"></div>
              </>
            )}
            {isStreaming ? (
              <button 
                onClick={handleStopGeneration}
                className='control-button stop-button'
                title="Stop generation"
              >
                <FeatherIcon icon="square" width="18px" height="18px" />
                <span>Stop</span>
              </button>
            ) : (
              <button 
                onClick={handleRefresh}
                className='control-button regenerate-button'
                title="Regenerate blog. The 1st and 2nd regeneration after your initial generation are free; later ones cost 5 credits."
                disabled={!h1Selected}
              >
                <FeatherIcon icon="refresh-cw" width="18px" height="18px" />
                <span>Regenerate</span>
              </button>
            )}
            
            <div className="split-button-wrap" ref={copyMenuRef}>
              <div className="split-button">
                <button
                  type="button"
                  onClick={() => {
                    handleCopyPlain();
                    setCopyMenuOpen(false);
                  }}
                  className={`control-button copy-button split-button__main ${copySuccess ? 'success' : ''}`}
                  title={copySuccess ? 'Copied!' : 'Copy plain text to clipboard'}
                  disabled={!currentContent}
                >
                  <div className="icon-container">
                    <FeatherIcon
                      icon="copy"
                      width="18px"
                      height="18px"
                      className={`copy-icon ${copySuccess ? 'hidden' : 'visible'}`}
                    />
                    <FeatherIcon
                      icon="check"
                      width="18px"
                      height="18px"
                      className={`check-icon ${copySuccess ? 'visible' : 'hidden'}`}
                    />
                  </div>
                  <span>Copy</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setDownloadMenuOpen(false);
                    setCopyMenuOpen((o) => !o);
                  }}
                  className="control-button copy-button split-button__toggle"
                  title="Copy as plain text or HTML"
                  disabled={!currentContent}
                  aria-expanded={copyMenuOpen}
                  aria-haspopup="menu"
                  aria-controls="copy-format-menu"
                >
                  <FeatherIcon
                    icon="chevron-down"
                    width="18px"
                    height="18px"
                    className={copyMenuOpen ? 'export-chevron--open' : ''}
                  />
                </button>
              </div>
              {copyMenuOpen && (
                <ul
                  className="export-dropdown"
                  id="copy-format-menu"
                  role="menu"
                  aria-label="Copy format"
                >
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="export-dropdown__item"
                      onClick={() => {
                        handleCopyPlain();
                        setCopyMenuOpen(false);
                      }}
                    >
                      Copy plain text
                    </button>
                  </li>
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="export-dropdown__item"
                      onClick={() => {
                        handleCopyHtml();
                        setCopyMenuOpen(false);
                      }}
                    >
                      Copy HTML
                    </button>
                  </li>
                </ul>
              )}
            </div>

            <div className="split-button-wrap" ref={downloadMenuRef}>
              <div className="split-button">
                <button
                  type="button"
                  onClick={() => {
                    setCopyMenuOpen(false);
                    setDownloadMenuOpen((o) => !o);
                  }}
                  className="control-button download-button split-button__main split-button__main--download"
                  title="Download blog (choose format)"
                  disabled={!currentContent}
                  aria-expanded={downloadMenuOpen}
                  aria-haspopup="menu"
                  aria-controls="download-format-menu"
                >
                  <FeatherIcon icon="download" width="18px" height="18px" />
                  <span>Download</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setCopyMenuOpen(false);
                    setDownloadMenuOpen((o) => !o);
                  }}
                  className="control-button download-button split-button__toggle"
                  title="Open download format menu"
                  disabled={!currentContent}
                  aria-label="Open download format menu"
                >
                  <FeatherIcon
                    icon="chevron-down"
                    width="18px"
                    height="18px"
                    className={downloadMenuOpen ? 'export-chevron--open' : ''}
                    aria-hidden
                  />
                </button>
              </div>
              {downloadMenuOpen && (
                <ul
                  className="export-dropdown"
                  id="download-format-menu"
                  role="menu"
                  aria-label="Download format"
                >
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="export-dropdown__item"
                      onClick={() => {
                        handleDownloadTxt();
                        setDownloadMenuOpen(false);
                      }}
                    >
                      Plain text (.txt)
                    </button>
                  </li>
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="export-dropdown__item"
                      onClick={() => {
                        handleDownloadHtml();
                        setDownloadMenuOpen(false);
                      }}
                    >
                      HTML (.html)
                    </button>
                  </li>
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="export-dropdown__item"
                      onClick={() => {
                        handleDownloadMd();
                        setDownloadMenuOpen(false);
                      }}
                    >
                      Markdown (.md)
                    </button>
                  </li>
                  <li role="none">
                    <button
                      type="button"
                      role="menuitem"
                      className="export-dropdown__item"
                      onClick={() => {
                        handleDownloadPdf();
                        setDownloadMenuOpen(false);
                      }}
                    >
                      PDF (print dialog)…
                    </button>
                  </li>
                </ul>
              )}
            </div>

            {isSaving && (
              <div className="save-indicator" title="Saving...">
                <FeatherIcon icon="upload-cloud" width="16px" height="16px" className="saving-icon" />
                <span>Saving...</span>
              </div>
            )}
          </div>
        </div>

        {regenBillingHint && (
          <div className="blog-regen-billing-hint" role="status">
            {regenBillingHint}
          </div>
        )}

        {regenToast && (
          <div className="blog-regen-toast" role="alert">
            {regenToast}
          </div>
        )}

        <div className={`editor-wrapper ${isStreaming ? 'streaming' : ''}`}>
          <EnhancementSidebar 
            onEnhancementSelect={handleEnhancementSelect}
            isGenerating={isGeneratingEnhancement}
          />
          
          <TiptapEditor
            ref={editorRef}
            initialContent={currentContent}
            onContentChange={handleContentChange}
            isStreaming={isStreaming}
          />
        </div>

        {isStreaming && (
          <div className="streaming-indicator">
            <FeatherIcon icon="radio" width="16px" height="16px" className="streaming-icon" />
            <span>Generating content...</span>
          </div>
        )}
      </div>

      {(seoTitle || seoDescription) && (
        <div className="seo-meta-panel">
          <div className="seo-meta-panel__header">
            <FeatherIcon icon="search" width="14px" height="14px" />
            <span>SEO Meta</span>
          </div>
          <div className="seo-meta-field">
            <label className="seo-meta-label">
              Title
              <span className={`seo-meta-count ${seoTitle.length > 60 ? 'seo-meta-count--over' : ''}`}>
                {seoTitle.length}/60
              </span>
            </label>
            <div className="seo-meta-value-row">
              <span className="seo-meta-value">{seoTitle}</span>
              <button
                className={`seo-meta-copy-btn ${seoTitleCopied ? 'seo-meta-copy-btn--copied' : ''}`}
                onClick={() => {
                  navigator.clipboard.writeText(seoTitle);
                  setSeoTitleCopied(true);
                  setTimeout(() => setSeoTitleCopied(false), 2000);
                }}
                title="Copy title"
              >
                <FeatherIcon icon={seoTitleCopied ? 'check' : 'copy'} width="14px" height="14px" />
              </button>
            </div>
          </div>
          <div className="seo-meta-field">
            <label className="seo-meta-label">
              Description
              <span className={`seo-meta-count ${seoDescription.length > 160 ? 'seo-meta-count--over' : ''}`}>
                {seoDescription.length}/160
              </span>
            </label>
            <div className="seo-meta-value-row">
              <span className="seo-meta-value">{seoDescription}</span>
              <button
                className={`seo-meta-copy-btn ${seoDescCopied ? 'seo-meta-copy-btn--copied' : ''}`}
                onClick={() => {
                  navigator.clipboard.writeText(seoDescription);
                  setSeoDescCopied(true);
                  setTimeout(() => setSeoDescCopied(false), 2000);
                }}
                title="Copy description"
              >
                <FeatherIcon icon={seoDescCopied ? 'check' : 'copy'} width="14px" height="14px" />
              </button>
            </div>
          </div>
        </div>
      )}

      <SettingsPanel />
    </div>
  );
}